package fr.manyverse.app.domain.validation

import fr.manyverse.app.domain.error.ValidationError

/**
 * Validates email addresses using a comprehensive regex pattern.
 *
 * This validator checks that email addresses:
 * - Have a local part (before @)
 * - Have an @ symbol
 * - Have a domain part (after @)
 * - Have a valid top-level domain (at least 2 characters)
 */
object EmailValidator {

    /**
     * Regex pattern for email validation.
     * Pattern: local-part@domain.tld
     * - Local part: alphanumeric and special chars (._%+-)
     * - Domain: alphanumeric and hyphens
     * - TLD: at least 2 letters
     */
    private val EMAIL_REGEX = "^(?!\\.)[\\w\\-_.+]+@[\\w-]+\\.[\\w.-]+[^.\\W]$".toRegex()

    /**
     * Validates an email address.
     *
     * @param email The email address to validate
     * @return true if the email is valid, false otherwise
     */
    fun isValid(email: String): Boolean {
        if (email.isBlank()) return false
        return EMAIL_REGEX.matches(email)
    }

    /**
     * Validates an email address and returns a Result.
     *
     * @param email The email address to validate
     * @return Result.success if the email is valid, Result.failure with ValidationError otherwise
     */
    fun validate(email: String): Result<Unit> {
        return if (!isValid(email)) {
            Result.failure(ValidationError("Invalid email format: must be a valid email address (e.g., user@example.com)"))
        } else {
            Result.success(Unit)
        }
    }
}
